import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../lib/store';
import { useAuthBootstrap } from '../lib/sessionBootstrap';
import { AppShellSkeleton } from './AppShellSkeleton';
import { NameGate } from '../features/auth/components/NameGate';

/**
 * Authentication gate for the desktop and mobile shells.
 *
 * What it renders:
 *   - status === 'loading'        → <AppShellSkeleton/> (no flash of gated UI)
 *   - status === 'unauthenticated'→ <Navigate to="/sign-in?next=..."/>
 *   - status === 'authenticated'  → children, unless the signed-in user has no
 *                                    display name: then <NameGate/> asks for one first
 *                                    (vendor#1436)
 *
 * Shell: (desktop) + (mobile). The (kiosk) shell MUST NOT be wrapped in this
 * gate — CLAUDE.md three-shell guardrail. This is NOT because kiosk sessions
 * are anonymous (they are not: kiosk keeps the real, already-authenticated
 * staff session live for the whole kiosk run — vendor#742), but because the
 * import-boundary ESLint rule, not session state, is what keeps staff-only
 * data hooks out of `(kiosk)` — wrapping it in `RequireAuth` would be
 * redundant at best.
 *
 * Non-obvious contracts:
 *   - Calls `useAuthBootstrap()` itself rather than expecting the caller to.
 *     Bootstrap runs once per RequireAuth tree; it is safe to mount this
 *     wrapper at the layout level (DesktopLayout / MobileLayout).
 *   - The `next` query param round-trips through SignInPage so a deep-link
 *     into a protected route survives the sign-in flow.
 */
export interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  useAuthBootstrap();
  const status = useSession((s) => s.status);
  const displayNameMissing = useSession((s) => s.displayNameMissing);
  const location = useLocation();

  if (status === 'loading') {
    return <AppShellSkeleton />;
  }

  if (status === 'unauthenticated') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }

  // A member needs a display name (vendor#1436). `displayNameMissing` is true
  // only when the profile read succeeded and was blank — never on a read error.
  if (displayNameMissing) return <NameGate />;

  return <>{children}</>;
}
